import FingerprintJS from "@fingerprintjs/fingerprintjs";
import {
  DECIMAL_NUMBER_REGX,
  US_CURRENCY_FORMAT,
  US_PHONE_FORMAT,
} from "../constants";

const getRecaptcha = (action) => {
  return new Promise((resolve, reject) => {
    // If channel is TOI, do NOT execute reCAPTCHA at all.
    try {
      const channelFromSession = window?.sessionStorage?.getItem("channel") || "";
      const channelFromUrl =
        typeof window !== "undefined"
          ? new URL(window.location.href).searchParams.get("channel") || ""
          : "";
      const channel = channelFromSession || channelFromUrl;
      if (channel === "TOI") {
        resolve(null);
        return;
      }
    } catch (e) {
      // ignore (fallback to normal behavior)
    }

    if (!window?.grecaptcha) {
      // Avoid hard-crash if grecaptcha is not loaded
      resolve(null);
      return;
    }
    window.grecaptcha.ready(function () {
      window.grecaptcha
        .execute(process.env.REACT_APP_GOOGLE_RECAPTCHA_KEY, {
          action: action,
        })
        .then((token) => {
          resolve(token);
        })
        .catch((error) => {
          reject(error);
        });
    });
  });
};

const getBrowserFingerprint = () => {
  // Get the visitor identifier when you need it.
  return new Promise((resolve, reject) => {
    const fpPromise = FingerprintJS.load();
    fpPromise
      .then((fp) => fp.get())
      .then((result) => {
        resolve(result.visitorId);
      })
      .catch((error) => {
        reject(error);
      });
  });
};

const saveRemoteConfig = (obj) => {
  localStorage.setItem("remoteConfig", JSON.stringify(obj));
};

const getRemoteConfig = () => {
  return localStorage.getItem("remoteConfig")
    ? JSON.parse(localStorage.getItem("remoteConfig"))
    : null;
};

const getParameterFromURL = (name, url) => {
  if (!url) url = window.location.href;
  name = name.replace(/[\[\]]/g, "\\$&");
  var regex = new RegExp("[?&]" + name + "(=([^&#]*)|&|#|$)"),
    results = regex.exec(url);
  if (!results) return null;
  if (!results[2]) return "";
  return decodeURIComponent(results[2].replace(/\+/g, " "));
};

const countdown = (time, ref) => {
  let timer = time;
  const timeInterval = setInterval(() => {
    if (timer > 0) {
      timer--;
      const getSeconds = `0${timer % 60}`.slice(-2);
      const minutes = `${Math.floor(timer / 60)}`;
      const getMinutes = `0${minutes % 60}`.slice(-2);
      // const getHours = `0${Math.floor(timer / 3600)}`.slice(-2);

      ref.current.innerText = `${getMinutes}m ${getSeconds}s`;
    }
  }, 1000);

  return () => {
    clearInterval(timeInterval);
  };
};

const formatPhoneNumber = (value) => {
  let tempVal = "";
  const input = value.replace(/\D/g, "").substring(0, 10);
  const zip = input.substring(0, 3);
  const middle = input.substring(3, 6);
  const last = input.substring(6, 10);
  if (input.length > 6) {
    tempVal = `(${zip}) ${middle}-${last}`;
  } else if (input.length > 3) {
    tempVal = `(${zip}) ${middle}`;
  } else if (input.length > 0) {
    tempVal = `(${zip}`;
  }
  return tempVal;
};

export const formatUsPhoneToString = (phone) => {
  const temp = ("" + phone).replace(/\D/g, "");
  return `+1${temp}`;
};

const validatePhone = (phone) => {
  let cleaned = ("" + phone).replace(/\D/g, "");

  const US_PHONE_FORMAT = /^(1|)?(\d{3})(\d{3})(\d{4})$/;

  const match = cleaned.match(US_PHONE_FORMAT);
  if (match) {
    return true;
  }
  return false;
};

export const NUMBER = /^[0-9]*$/;

const formatCurrency = (number, formatType, formatDecimal = false) => {
  const hasDecimal = number.indexOf(".") > -1;
  if (!hasDecimal) {
    return new Intl.NumberFormat(formatType).format(number);
  }
  const [integerPortion, decimalPortion] = number.split(".");
  const formattedIntegerPortion = new Intl.NumberFormat(formatType).format(
    integerPortion
  );
  if (formatDecimal) {
    const decimal =
      decimalPortion?.length === 1 ? `${decimalPortion}0` : decimalPortion;
    return `${formattedIntegerPortion}.${decimal}`;
  }

  return `${formattedIntegerPortion}.${decimalPortion}`;
};

const removeDecimal = (amount, formatType) => {
  if (amount || amount === 0) {
    const convertedAmount = parseFloat(amount?.toString().replace(/,/g, ""));
    const formattedAmount = formatCurrency(
      `${convertedAmount.toLocaleString("fullwide", { useGrouping: false })}`,
      formatType,
      true
    );
    return formattedAmount;
  }
};

// Prefer v2 if available, fallback to old
export function getFxRateData(exchangeData) {
  return (
    exchangeData?.transaction_wise_fx_rate_v2 ||
    exchangeData?.transaction_wise_fx_rate ||
    {}
  );
}

// Ported handleBlendedFxRates from neobank-react
export const handleBlendedFxRates = (
  inputAmount,
  exchangeData,
  currencyType
) => {
  const breakdown = [];
  let unlockInfo = null;
  currencyType = currencyType || "USD";

  // Pick new field if available, else fallback to old one
  const {
    transaction_wise_fx_rate_v2,
    transaction_wise_fx_rate,
    max_amount_transaction,
  } = exchangeData || {};

  const fxRates = transaction_wise_fx_rate_v2 || transaction_wise_fx_rate;
  if (!fxRates) return { breakdown, unlockInfo };

  const convertToNumber = (amount) =>
    parseFloat(amount?.toString().replace(/,/g, ""));

  let totalInput = convertToNumber(convertToNumber(inputAmount).toFixed(2));
  let remaining = totalInput;

  if (!DECIMAL_NUMBER_REGX.test(totalInput)) return { breakdown, unlockInfo };

  const keys = Object.keys(fxRates);

  // ---------- CASE 1: New v2 schema ----------
  if (transaction_wise_fx_rate_v2) {
    // Separate TIERED and SLABS
    const tieredKey = keys.find(
      (k) => fxRates[k].campaign_calculation_type === "TIERED"
    );
    const slabs = keys.filter(
      (k) => fxRates[k].campaign_calculation_type !== "TIERED"
    );

    // Apply TIERED first
    if (tieredKey) {
      const tiered = fxRates[tieredKey];
      const tieredMax = tiered.amount;
      let tempremaining = remaining;
      if (currencyType == "INR") {
        tempremaining = parseFloat(
          (remaining / tiered.effective_exchange_rate).toFixed(2)
        );
      }
      let applyAmount = 0;
      if (tempremaining >= tieredMax) {
        applyAmount = tieredMax;
      } else {
        applyAmount = tempremaining;
      }

      if (applyAmount > 0) {
        breakdown.push({
          source_amount: parseFloat(applyAmount.toFixed(2)),
          destination_amount: parseFloat(
            (applyAmount * tiered.effective_exchange_rate).toFixed(2)
          ),
          effective_fx_rate: tiered.effective_exchange_rate,
          campaign: "Promotional FX Rate",
          campaign_id: tiered.campaign_id,
          display_effective_rate:
            tiered.effective_exchange_rate_data.display_value,
          display_source_amount: removeDecimal(
            parseFloat(applyAmount.toFixed(2)),
            US_CURRENCY_FORMAT
          ),
          promotional_applicable: true,
        });
        if (currencyType == "INR") {
          remaining -= applyAmount * tiered.effective_exchange_rate;
        } else {
          remaining -= applyAmount;
        }
      }
    }

    // Apply slab if still remaining
    if (remaining > 0 && slabs.length > 0) {
      let eligibleSlabs = [];
      if (currencyType == "INR") {
        eligibleSlabs = slabs
          .map((k) => fxRates[k])
          .filter((s) => totalInput / s.effective_exchange_rate >= s.amount);
      } else {
        eligibleSlabs = slabs
          .map((k) => fxRates[k])
          .filter((s) => totalInput >= s.amount);
      }

      if (eligibleSlabs.length > 0) {
        const slab = eligibleSlabs.sort((a, b) => b.amount - a.amount)[0];
        let sourceAmount = parseFloat(remaining.toFixed(2));
        if (currencyType == "INR") {
          sourceAmount = parseFloat(
            (remaining / slab.effective_exchange_rate).toFixed(2)
          );
        }
        breakdown.push({
          source_amount: sourceAmount,
          destination_amount: parseFloat(
            (remaining * slab.effective_exchange_rate).toFixed(2)
          ),
          effective_fx_rate: slab.effective_exchange_rate,
          campaign: "Best FX Guaranteed",
          campaign_id: slab.campaign_id,
          display_effective_rate:
            slab.effective_exchange_rate_data.display_value,
          display_source_amount: removeDecimal(
            parseFloat(sourceAmount.toFixed(2)),
            US_CURRENCY_FORMAT
          ),
          promotional_applicable: false,
        });
      }
    }

    // unlockInfo & average rate
    let nextSlab = null;
    let nextCompleteSlab = null;
    let averageRate = 0;

    if (breakdown.length > 0) {
      const totalSource = breakdown.reduce(
        (sum, item) => sum + item.source_amount,
        0
      );
      const totalDest = breakdown.reduce(
        (sum, item) => sum + item.destination_amount,
        0
      );
      averageRate = totalSource > 0 ? totalDest / totalSource : 0;
    }

    const round2 = (num) => parseFloat(num.toFixed(2));
    if (remaining > 0 && slabs.length > 0) {
      const slabsArray = slabs.map((k) => fxRates[k]);
      const nextSlabArray = slabsArray.sort((a, b) => a.amount - b.amount);

      // Convert totalInput to USD for comparison if currency is INR
      let comparisonAmount = totalInput;
      if (currencyType == "INR") {
        // Use the first available slab rate for conversion, or fallback to 1
        const conversionRate =
          nextSlabArray.length > 0
            ? nextSlabArray[0].effective_exchange_rate
            : 1;
        comparisonAmount = totalInput / conversionRate;
      }

      for (let i = 0; i < nextSlabArray.length; i++) {
        if (comparisonAmount >= nextSlabArray[i].threshold_value) {
          nextSlab = nextSlabArray[i];
        }
      }
      for (let i = 0; i < nextSlabArray.length; i++) {
        if (
          comparisonAmount >= nextSlabArray[i].amount &&
          comparisonAmount >= nextSlabArray[i].threshold_value
        ) {
          nextCompleteSlab = nextSlabArray[i];
        }
      }
      if (nextSlab && nextSlab.amount >= comparisonAmount) {
        unlockInfo = {
          amountToUnlock: nextSlab.amount - comparisonAmount,
          unlockRate: nextSlab.effective_exchange_rate,
          unlockThreshold: nextSlab.amount,
          promoUnlocked: false,
          averageRate: round2(averageRate),
        };
        sessionStorage.setItem("averageRateV2", unlockInfo.averageRate);
      } else if (nextCompleteSlab && sessionStorage.getItem("averageRateV2")) {
        unlockInfo = {
          amountToUnlock: nextCompleteSlab.amount - comparisonAmount,
          unlockRate: nextCompleteSlab.effective_exchange_rate,
          unlockThreshold: nextCompleteSlab.amount,
          promoUnlocked: true,
          averageRate: sessionStorage.getItem("averageRateV2"),
        };
      }
    }
  }

  // ---------- CASE 2: Old schema ----------
  else if (transaction_wise_fx_rate) {
    const txnWiseKeys = Object.keys(fxRates || {});
    const len = txnWiseKeys?.length;
    const lastTxnWiseKey = txnWiseKeys[len - 1];

    for (let key in fxRates) {
      let {
        max_amount_transaction: max_amount,
        promotional_applicable,
        effective_exchange_rate_data: {
          display_value: display_effective_exchange_rate,
          value: effective_exchange_rate,
        } = {},
      } = fxRates[key] || {};

      if (remaining > 0) {
        if (currencyType === "USD") {
          if (totalInput <= max_amount_transaction) {
            if (key === lastTxnWiseKey) {
              breakdown.push({
                source_amount: parseFloat(remaining.toFixed(2)),
                destination_amount: parseFloat(
                  (remaining * effective_exchange_rate).toFixed(2)
                ),
                effective_fx_rate: effective_exchange_rate,
                campaign: promotional_applicable
                  ? "Promotional FX Rate"
                  : "Best FX Guaranteed",
                campaign_id: fxRates[key].campaign_id,
                display_effective_rate: display_effective_exchange_rate,
                display_source_amount: removeDecimal(
                  parseFloat(remaining.toFixed(2)),
                  US_CURRENCY_FORMAT
                ),
                promotional_applicable,
              });
              remaining = 0;
              break;
            }
            breakdown.push({
              source_amount:
                remaining <= max_amount
                  ? parseFloat(remaining.toFixed(2))
                  : parseFloat(max_amount.toFixed(2)),
              destination_amount:
                remaining <= max_amount
                  ? parseFloat((remaining * effective_exchange_rate).toFixed(2))
                  : parseFloat(
                      (max_amount * effective_exchange_rate).toFixed(2)
                    ),
              effective_fx_rate: effective_exchange_rate,
              campaign: promotional_applicable
                ? "Promotional FX Rate"
                : "Best FX Guaranteed",
              campaign_id: fxRates[key].campaign_id,
              display_effective_rate: display_effective_exchange_rate,
              display_source_amount:
                remaining <= max_amount
                  ? removeDecimal(
                      parseFloat(remaining.toFixed(2)),
                      US_CURRENCY_FORMAT
                    )
                  : removeDecimal(
                      parseFloat(max_amount.toFixed(2)),
                      US_CURRENCY_FORMAT
                    ),
              promotional_applicable,
            });
            remaining = max_amount >= remaining ? 0 : remaining - max_amount;
          }
        } else {
          if (key === lastTxnWiseKey) {
            breakdown.push({
              source_amount: parseFloat(
                (remaining / effective_exchange_rate).toFixed(2)
              ),
              destination_amount: parseFloat(remaining.toFixed(2)),
              effective_fx_rate: effective_exchange_rate,
              campaign: promotional_applicable
                ? "Promotional FX Rate"
                : "Best FX Guaranteed",
              campaign_id: fxRates[key].campaign_id,
              display_effective_rate: display_effective_exchange_rate,
              display_source_amount: removeDecimal(
                parseFloat((remaining / effective_exchange_rate).toFixed(2)),
                US_CURRENCY_FORMAT
              ),
              promotional_applicable,
            });
            remaining = 0;
            break;
          }
          breakdown.push({
            source_amount: parseFloat(
              (
                Math.min(max_amount * effective_exchange_rate, remaining) /
                effective_exchange_rate
              ).toFixed(2)
            ),
            destination_amount: parseFloat(
              Math.min(max_amount * effective_exchange_rate, remaining).toFixed(
                2
              )
            ),
            effective_fx_rate: effective_exchange_rate,
            campaign: promotional_applicable
              ? "Promotional FX Rate"
              : "Best FX Guaranteed",
            campaign_id: fxRates[key].campaign_id,
            display_effective_rate: display_effective_exchange_rate,
            display_source_amount: removeDecimal(
              parseFloat(
                (
                  Math.min(max_amount * effective_exchange_rate, remaining) /
                  effective_exchange_rate
                ).toFixed(2)
              ),
              US_CURRENCY_FORMAT
            ),
            promotional_applicable,
          });
          remaining =
            max_amount * effective_exchange_rate >= remaining
              ? 0
              : remaining - max_amount * effective_exchange_rate;
        }
      }
    }
  }

  return { breakdown, unlockInfo };
};

// Helper: Given a target INR, find the USD amount that would result in that INR using slabs
export const getUsdForInr = (
  targetInr,
  exchangeData,
  precision = 0.01,
  maxIterations = 30
) => {
  let low = 0;
  let high = 1e9; // better upper bound if available
  let result = 0;

  for (let i = 0; i < maxIterations; i++) {
    const mid = (low + high) / 2;
    const { breakdown } = handleBlendedFxRates(mid, exchangeData);
    const totalInr = breakdown.reduce(
      (sum, item) => sum + item.destination_amount,
      0
    );

    if (Math.abs(totalInr - targetInr) <= precision) {
      return parseFloat(mid.toFixed(2)); // found exact match
    }

    if (totalInr < targetInr) {
      low = mid;
    } else {
      high = mid;
      result = mid; // best so far
    }
  }

  // Return closest found result or fallback
  if (result > 0) {
    return parseFloat(result.toFixed(2));
  }
  return exchangeData?.min_amount_transaction || 0;
};

const format2Decimals = (number) => {
  if (number) {
    return Math.round(number * 100) / 100;
  }
  return number;
};

const isMobileDevice = () => {
  let check = false;
  (function (a) {
    if (
      /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(
        a
      ) ||
      /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(
        a.substr(0, 4)
      )
    )
      check = true;
  })(navigator.userAgent || navigator.vendor || window.opera);
  return check;
};

const openExternalLink = (url) => {
  if (window.sessionStorage.getItem("channel") === "TOI") {
    if (
      typeof window.webkit?.messageHandlers?.tilAppWebBridge !== "undefined"
    ) {
      window.webkit.messageHandlers.tilAppWebBridge.postMessage({
        requestReason: "aboundLogin",
        type: "deeplink",
        value: `toiapp://open-$|$-type=sf-$|$-lang=1-$|$-url=${url}`,
      });
    }
    if (typeof window.tilAppWebBridge !== "undefined") {
      window.tilAppWebBridge.postMessage(
        JSON.stringify({
          requestReason: "aboundLogin",
          type: "deeplink",
          value: `toiapp://open-$|$-id=Abound-01-$|$-lang=1-$|$-url=${url}-$|$-type=htmlview`,
        })
      );
    }
  }
};

const downloadApp = (type, deeplink = "") => {
  let url = "";
  if (type === "iOS") {
    url = "https://abound.onelink.me/P9V6/remittanceweb";
  }
  if (type === "android") {
    url = "https://abound.onelink.me/P9V6/remittanceweb";
  }
  if (window.sessionStorage.getItem("channel") === "TOI") {
    if (
      typeof window.webkit?.messageHandlers?.tilAppWebBridge !== "undefined"
    ) {
      window.webkit.messageHandlers.tilAppWebBridge.postMessage({
        requestReason: "aboundLogin",
        type: "deeplink",
        value:
          deeplink || "toiapp://open-$|$-type=sf-$|$-lang=1-$|$-url=" + url,
      });
    }
    if (typeof window.tilAppWebBridge !== "undefined") {
      window.tilAppWebBridge.postMessage(
        JSON.stringify({
          requestReason: "aboundLogin",
          type: "deeplink",
          value:
            deeplink ||
            "toiapp://open-$|$-id=Abound-01-$|$-lang=1-$|$-url=https://abound.onelink.me/P9V6/remittanceweb-$|$-type=htmlview",
        })
      );
    }
  } else {
    window.open(url, "_blank");
  }
};

const formatUsNumber = (phone) => {
  let cleaned = ("" + phone).replace(/\D/g, "");
  const match = cleaned.match(US_PHONE_FORMAT);
  const isMatched = US_PHONE_FORMAT.test(cleaned);
  if (isMatched) {
    const formattedPhone = ["(", match[2], ") ", match[3], "-", match[4]].join(
      ""
    );

    return formattedPhone;
  } else {
    return "";
  }
};

const goToAbound = () => {
  window.open(process.env.REACT_APP_ABOUND_SITE, "_self");
};

export {
  getRecaptcha,
  getBrowserFingerprint,
  saveRemoteConfig,
  getRemoteConfig,
  getParameterFromURL,
  countdown,
  formatPhoneNumber,
  validatePhone,
  formatCurrency,
  format2Decimals,
  isMobileDevice,
  downloadApp,
  formatUsNumber,
  goToAbound,
  openExternalLink,
};
